import { useEffect, useRef } from 'react';

import { isNotProductionEnvironment } from '@treneo/environments';
import { authApi, LoginState, performLogout, useSelectLoginState } from '@treneo/features/auth';
import { logger } from '@treneo/shared/logger';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const useTokenRefresh = () => {
  const loginState = useSelectLoginState();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loginStateRef = useRef(loginState);

  useEffect(() => {
    loginStateRef.current = loginState;
  }, [loginState]);

  useEffect(() => {
    const stopRefresh = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const startRefresh = () => {
      stopRefresh();

      intervalRef.current = setInterval(async () => {
        const currentLoginState = loginStateRef.current;
        if (
          currentLoginState === LoginState.LOGGING_OUT ||
          currentLoginState === LoginState.NOT_LOGGED ||
          currentLoginState === LoginState.LOGIN_REJECTED
        ) {
          stopRefresh();
          return;
        }

        try {
          const isExpiringSoon = await authApi.isTokenExpiringSoon(5);
          if (!isExpiringSoon) {
            return;
          }

          if (isNotProductionEnvironment()) {
            logger.debug('[TokenRefresh] Token expiring soon, refreshing...');
          }

          await authApi.refreshToken();
        } catch (error: unknown) {
          const err = error as { response?: { status?: number; headers?: Record<string, string> } };
          const status = err?.response?.status;
          if (status === 401) {
            const retryAfter = err?.response?.headers?.['retry-after'];
            if (retryAfter && parseInt(retryAfter) > 0) {
              logger.warn(
                `[TokenRefresh] Token refresh failed with 401, retrying after ${retryAfter} seconds`
              );
              return;
            }
            logger.warn('[TokenRefresh] Token refresh failed with 401, logging out');
            void performLogout();
            stopRefresh();
          } else if (status && status >= 500) {
            logger.warn('[TokenRefresh] Server error during refresh, will retry:', error);
          } else if (!err?.response) {
            logger.warn('[TokenRefresh] Network error during refresh, will retry:', error);
          } else {
            logger.warn('[TokenRefresh] Active refresh failed:', error);
          }
        }
      }, REFRESH_INTERVAL_MS);
    };

    if (loginState === LoginState.LOGGED) {
      startRefresh();
    } else {
      stopRefresh();
    }

    return () => stopRefresh();
  }, [loginState]);
};
